/**
 * Picks Analytics Grid
 * Renders all leg-level performance breakdown tables for picks mode.
 */
import type { ReactElement } from 'react';
import type { LabDashboardData } from '../hooks/labDashboardTypes';
import type { UseLegOriginPerformanceResult } from '../hooks/useLabMarket';
import { PerformanceTable } from './PerformanceTable';

interface PicksAnalyticsGridProps {
  data: LabDashboardData;
  legOriginPerformance: UseLegOriginPerformanceResult;
}

export function PicksAnalyticsGrid({
  data,
  legOriginPerformance,
}: PicksAnalyticsGridProps): ReactElement {
  return (
    <>
      {/* Config Performance */}
      {data.configPerformance.length > 0 && (
        <PerformanceTable
          title="By Config"
          data={data.configPerformance}
          columns={[
            { key: 'configVariation', label: 'Config' },
            { key: 'hits', label: 'W', width: 'w-12' },
            { key: 'misses', label: 'L', width: 'w-12' },
            { key: 'hitRate', label: 'Hit %', format: 'percent', width: 'w-16' },
            { key: 'roi', label: 'ROI', format: 'roi', width: 'w-16' },
          ]}
        />
      )}

      {/* Tier Performance */}
      {data.tierPerformance.length > 0 && (
        <PerformanceTable
          title="By Tier"
          data={data.tierPerformance}
          columns={[
            { key: 'tier', label: 'Tier' },
            { key: 'hits', label: 'W', width: 'w-12' },
            { key: 'misses', label: 'L', width: 'w-12' },
            { key: 'hitRate', label: 'Hit %', format: 'percent', width: 'w-16' },
          ]}
        />
      )}

      {/* Prop Type Performance */}
      {data.propTypePerformance.length > 0 && (
        <PerformanceTable
          title="By Prop Type"
          data={data.propTypePerformance}
          columns={[
            { key: 'propType', label: 'Prop', format: 'statLabel' },
            { key: 'hits', label: 'W', width: 'w-12' },
            { key: 'misses', label: 'L', width: 'w-12' },
            { key: 'hitRate', label: 'Hit %', format: 'percent', width: 'w-16' },
          ]}
        />
      )}

      {/* Direction Performance */}
      {data.directionPerformance.length > 0 && (
        <PerformanceTable
          title="By Over/Under"
          data={data.directionPerformance}
          columns={[
            { key: 'direction', label: 'Direction' },
            { key: 'hits', label: 'W', width: 'w-12' },
            { key: 'misses', label: 'L', width: 'w-12' },
            { key: 'hitRate', label: 'Hit %', format: 'percent', width: 'w-16' },
            { key: 'roi', label: 'ROI', format: 'roi', width: 'w-16' },
          ]}
        />
      )}

      {/* AI Edge Performance */}
      {data.edgePerformance.length > 0 && (
        <PerformanceTable
          title="By AI Edge"
          data={data.edgePerformance}
          columns={[
            { key: 'edge', label: 'Edge' },
            { key: 'hits', label: 'W', width: 'w-12' },
            { key: 'misses', label: 'L', width: 'w-12' },
            { key: 'hitRate', label: 'Hit %', format: 'percent', width: 'w-16' },
            { key: 'roi', label: 'ROI', format: 'roi', width: 'w-16' },
          ]}
        />
      )}

      {/* AI Confidence Performance */}
      {data.confidencePerformance.length > 0 && (
        <PerformanceTable
          title="By AI Confidence"
          data={data.confidencePerformance}
          columns={[
            { key: 'confidence', label: 'Confidence' },
            { key: 'hits', label: 'W', width: 'w-12' },
            { key: 'misses', label: 'L', width: 'w-12' },
            { key: 'hitRate', label: 'Hit %', format: 'percent', width: 'w-16' },
            { key: 'roi', label: 'ROI', format: 'roi', width: 'w-16' },
          ]}
        />
      )}

      {/* AI Trend Performance */}
      {data.trendPerformance.length > 0 && (
        <PerformanceTable
          title="By AI Trend"
          data={data.trendPerformance}
          columns={[
            { key: 'trend', label: 'Trend' },
            { key: 'hits', label: 'W', width: 'w-12' },
            { key: 'misses', label: 'L', width: 'w-12' },
            { key: 'hitRate', label: 'Hit %', format: 'percent', width: 'w-16' },
            { key: 'roi', label: 'ROI', format: 'roi', width: 'w-16' },
          ]}
        />
      )}

      {/* Model Performance */}
      {data.modelPerformance.length > 0 && (
        <PerformanceTable
          title="By Model"
          data={data.modelPerformance}
          columns={[
            { key: 'model', label: 'Model' },
            { key: 'hits', label: 'W', width: 'w-12' },
            { key: 'misses', label: 'L', width: 'w-12' },
            { key: 'hitRate', label: 'Hit %', format: 'percent', width: 'w-16' },
            { key: 'roi', label: 'ROI', format: 'roi', width: 'w-16' },
          ]}
        />
      )}

      {/* B2B Performance */}
      {data.b2bPerformance.length > 0 && (
        <PerformanceTable
          title="Back-to-Back Analysis"
          data={data.b2bPerformance}
          columns={[
            { key: 'category', label: 'Category' },
            { key: 'hits', label: 'W', width: 'w-12' },
            { key: 'misses', label: 'L', width: 'w-12' },
            { key: 'hitRate', label: 'Hit %', format: 'percent', width: 'w-16' },
            { key: 'roi', label: 'ROI', format: 'roi', width: 'w-16' },
          ]}
        />
      )}

      {/* Blowout Risk Performance */}
      {data.blowoutRiskPerformance.length > 0 && (
        <PerformanceTable
          title="By Blowout Risk"
          data={data.blowoutRiskPerformance}
          columns={[
            { key: 'risk', label: 'Risk Level' },
            { key: 'hits', label: 'W', width: 'w-12' },
            { key: 'misses', label: 'L', width: 'w-12' },
            { key: 'hitRate', label: 'Hit %', format: 'percent', width: 'w-16' },
            { key: 'roi', label: 'ROI', format: 'roi', width: 'w-16' },
          ]}
        />
      )}

      {/* Line Range Performance */}
      {data.lineRangePerformance?.length > 0 && (
        <PerformanceTable
          title="By Line Range"
          data={data.lineRangePerformance}
          columns={[
            { key: 'lineRange', label: 'Line Range' },
            { key: 'hits', label: 'W', width: 'w-12' },
            { key: 'misses', label: 'L', width: 'w-12' },
            { key: 'hitRate', label: 'Hit %', format: 'percent', width: 'w-16' },
            { key: 'roi', label: 'ROI', format: 'roi', width: 'w-16' },
            { key: 'avgLine', label: 'Avg', format: 'number', width: 'w-14' },
          ]}
        />
      )}

      {/* Prop Type × Line Range Performance */}
      {data.propTypeLineRangePerformance?.length > 0 && (
        <PerformanceTable
          title="By Prop Type × Line Range"
          data={data.propTypeLineRangePerformance}
          columns={[
            { key: 'propType', label: 'Prop', format: 'statLabel' },
            { key: 'lineRange', label: 'Range' },
            { key: 'hits', label: 'W', width: 'w-12' },
            { key: 'misses', label: 'L', width: 'w-12' },
            { key: 'hitRate', label: 'Hit %', format: 'percent', width: 'w-16' },
            { key: 'roi', label: 'ROI', format: 'roi', width: 'w-16' },
          ]}
        />
      )}

      {/* Direction × Line Range Performance */}
      {data.lineDirectionPerformance?.length > 0 && (
        <PerformanceTable
          title="By Direction × Line Range"
          data={data.lineDirectionPerformance}
          columns={[
            { key: 'direction', label: 'Dir' },
            { key: 'lineRange', label: 'Range' },
            { key: 'hits', label: 'W', width: 'w-12' },
            { key: 'misses', label: 'L', width: 'w-12' },
            { key: 'hitRate', label: 'Hit %', format: 'percent', width: 'w-16' },
            { key: 'roi', label: 'ROI', format: 'roi', width: 'w-16' },
          ]}
        />
      )}

      {/* Leg-Level Origin Performance (AI vs Manual vs Imported Bets) */}
      {legOriginPerformance.originPerformance.length > 0 && (
        <PerformanceTable
          title="By Origin (Leg-Level)"
          data={legOriginPerformance.originPerformance}
          columns={[
            { key: 'origin', label: 'Origin' },
            { key: 'hits', label: 'W', width: 'w-12' },
            { key: 'misses', label: 'L', width: 'w-12' },
            { key: 'hitRate', label: 'Hit %', format: 'percent', width: 'w-16' },
            { key: 'settledLegs', label: 'Settled', width: 'w-16' },
            { key: 'verifiedBets', label: 'Verified', width: 'w-16' },
          ]}
        />
      )}

      {/* Prop Type × Origin Performance */}
      {legOriginPerformance.statTypeByOrigin.length > 0 && (
        <PerformanceTable
          title="By Prop Type × Origin"
          data={legOriginPerformance.statTypeByOrigin}
          columns={[
            { key: 'statType', label: 'Prop', format: 'statLabel' },
            { key: 'origin', label: 'Origin' },
            { key: 'hits', label: 'W', width: 'w-12' },
            { key: 'misses', label: 'L', width: 'w-12' },
            { key: 'hitRate', label: 'Hit %', format: 'percent', width: 'w-16' },
          ]}
        />
      )}
    </>
  );
}
